// https://gist.github.com/beaucharman/e46b8e4d03ef30480d7f4db5a78498ca

export function throttle<
  R,
  A extends any[] = unknown[],
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  C = {},
>(
  callback: (this: C | ThisType<typeof throttle>, ...args: A) => R,
  waitMs: number,
  context?: C
) {
  let lastExec = 0;
  let timeoutID: undefined | number;
  let callbackArgs: A;
  let lastResult: R;

  const later = () => {
    lastExec = Date.now();
    timeoutID = undefined;
    lastResult = callback.apply(context || {}, callbackArgs);
  };

  return function handleThrottledInvoke(...args: any) {
    callbackArgs = args;
    if (!timeoutID) {
      timeoutID =
        Date.now() - lastExec >= waitMs
          ? window.setTimeout(later, 0)
          : window.setTimeout(later, waitMs);
    }
    return lastResult;
  };
}

/**
 * Execute only when no request initialized in given `waitMs` interval.
 * Note that function will be always called with initial delay!
 * But it will only trigger after silent time gap.
 */
export const debouncerByKey = () => {
  const cachedTimeouts: {
    [key: string]: undefined | number;
  } = {};
  return function debounceByKey(
    key: string,
    waitMs: number,
    callback: () => void
  ) {
    const lastExecTimeoutId = cachedTimeouts[key];

    if (lastExecTimeoutId) {
      // Trash existing callback for same key
      clearTimeout(lastExecTimeoutId);
    }

    cachedTimeouts[key] = window.setTimeout(() => {
      cachedTimeouts[key] = undefined;
      callback();
    }, waitMs);
  };
};

/**
 * First run: Immediate
 * Next runs at `timeWindowMs` period (compared to debounce, throttle will be triggered continuously)
 * Last run: Always trigger when `trailingEnabled` = true (default)
 */
export const throttleInstance = (
  timeWindowMs: number,
  trailingEnabled = true
) => {
  const last = {
    execution: new Date().getTime() - timeWindowMs,
  };
  let lastTimetoutId: number | undefined;
  return (callback: () => void): void => {
    if (lastTimetoutId) {
      window.clearTimeout(lastTimetoutId);
      lastTimetoutId = undefined;
    }
    const now = Date.now();
    if (last.execution + timeWindowMs <= now) {
      last.execution = now;
      callback();
      return;
    }
    if (!trailingEnabled) {
      return;
    }
    const currentLastExecution = last.execution;
    lastTimetoutId = window.setTimeout(() => {
      // console.log('Trying last', currentLastExecution, last.execution);
      if (currentLastExecution <= last.execution) {
        // If wasn't executed, execute last one
        callback();
      }
    }, timeWindowMs);
  };
};

/**
 * A special throttle which guarantees that a single execution of the given async function will ever be
 * run at any given time.  It will throttle the function so that it never executes more than once every
 * minWaitMs but will not attempt to reschedule the next execution until the previous execution has
 * resolved.
 *
 * @example
 *  const fn = throttleUnique(async () => {
 *    // This fn will only ever have a single execution running
 *    // and will run at most once every 10 seconds.
 *  }, 10000)
 */
// export function throttleUnique<
//   R,
//   A extends any[] = unknown[],
//   // eslint-disable-next-line @typescript-eslint/ban-types
//   C = {}
//   // eslint-disable-next-line @typescript-eslint/ban-types
// >(callback: (this: C | {}, ...args: A) => R, minWaitMs = 0, context?: C) {
//   let inProgress = false;
//   let nextDoExecTimeoutID: number | undefined;
//   let lastResultTimeoutID: number | undefined;

//   let lastExec = 0;
//   let lastRequested = 0;
//   let lastCancel = 0;
//   let callbackArgs: A;

//   let lastResult: Promise<R> | undefined;

//   const getTimeoutMs = (now = Date.now()) =>
//     lastCancel >= lastRequested ? 0 : Math.max(minWaitMs - (now - lastExec), 0);

//   const scheduleNextIfNeeded = () => {
//     inProgress = false;
//     // eslint-disable-next-line @typescript-eslint/no-misused-promises
//     if (lastResult && lastRequested <= lastExec) {
//       return;
//     }
//     const timeoutMs = getTimeoutMs();
//     if (timeoutMs === 0 && !nextDoExecTimeoutID) {
//       // we need to return a promise that resolves to the next execution not the
//       // previous so we need to execute this immediately in the case that we are
//       // timed out and do not have another execution scheduled already.
//       doExec();
//     } else {
//       nextDoExecTimeoutID = window.setTimeout(doExec, timeoutMs);
//     }
//   };

//   const doExec = () => {
//     clearTimeout(nextDoExecTimeoutID);
//     nextDoExecTimeoutID = undefined;
//     lastExec = Date.now();

//     inProgress = true;

//     lastResult = new Promise((resolve, reject) => {
//       lastResultTimeoutID = window.setTimeout(() => {
//         lastResultTimeoutID = undefined;
//         Promise.resolve(callback.apply(context || {}, callbackArgs)).then(
//           result => {
//             if (lastCancel >= lastExec) {
//               reject('Callback Canceled');
//             } else {
//               resolve(result);
//             }
//           }
//         );
//       }, 0);
//     });
//     lastResult.finally(scheduleNextIfNeeded);
//   };

//   async function handleInvokeThrottleUnique(
//     ...args: A
//   ): Promise<R | undefined> {
//     callbackArgs = args;
//     lastRequested = Date.now();
//     if (!inProgress && !nextDoExecTimeoutID) {
//       scheduleNextIfNeeded();
//     }
//     return lastResult;
//   }

//   /**
//    * Only schedules or runs the function if the last request has completed AND
//    * the min wait has expired after the execution was finished running, otherwise
//    * will always return the result of the last run.
//    *
//    * This, for example, would be useful if you are depending on the promise
//    * that is returned by the call to ifNeeded() to get your results rather
//    * than simply executing some action in the throttled function body.
//    *
//    * @example
//    *  // this would only execute a single time in this example while both 1 and 2 would
//    *  // receive 1 as their result
//    *  async function throttleExample(n: number) {
//    *    console.log('Throttle Executes! ', n)
//    *    return n;
//    *  }
//    *  const throttledFn = throttleUnique(throttleExample, 10000);
//    *  throttledFn.ifNeeded().then(() => {
//    *    console.log('1 Executes');
//    *  })
//    *  throttledFn.ifNeeded().then(() => {
//    *   console.log('2 Executes')
//    *  })
//    */
//   handleInvokeThrottleUnique.ifNeeded = async (
//     ...args: A
//   ): Promise<R | undefined> => {
//     if (
//       lastResult &&
//       (inProgress ||
//         nextDoExecTimeoutID ||
//         (getTimeoutMs() > 0 && lastCancel >= lastExec))
//     ) {
//       // we do not attempted to reschedule if a previous is running
//       return lastResult;
//     }
//     return handleInvokeThrottleUnique(...args);
//   };

//   handleInvokeThrottleUnique.getTimeoutMs = getTimeoutMs;

//   /**
//    * Cancels any executions which are scheduled to run. In addition, this will reject any
//    * promises waiting for a result
//    */
//   handleInvokeThrottleUnique.cancel = () => {
//     clearTimeout(nextDoExecTimeoutID);
//     clearTimeout(lastResultTimeoutID);
//     nextDoExecTimeoutID = undefined;
//     lastResultTimeoutID = undefined;

//     lastCancel = Date.now();
//     inProgress = false;
//   };

//   /**
//    * Removes the lastResult so that it will not be returned during future invocations
//    */
//   handleInvokeThrottleUnique.reset = () => {
//     lastResult = undefined;
//   };

//   return handleInvokeThrottleUnique;
// }
